// Create new file: src/services/InventoryDeductionService.js

import { doc, getDoc, updateDoc, setDoc, collection, getDocs } from 'firebase/firestore';
import { db } from './firebaseConfig';

const InventoryDeductionService = {
  /**
   * Deduct inventory when order is placed by customer
   * @param {string} restaurantId - Restaurant ID
   * @param {array} orderItems - Array of ordered items with quantities
   * @param {string} orderId - Order ID for tracking
   */
  async deductInventoryForOrder(restaurantId, orderItems, orderId) {
    try {
      console.log('🔄 Processing inventory deduction for order:', orderId);
      
      const deductionLog = {
        orderId,
        timestamp: new Date().toISOString(),
        items: [],
        success: [],
        failures: []
      };

      for (const orderItem of orderItems) {
        const { itemId, quantity, portion } = orderItem;
        
        // Get menu item to find recipe
        const menuItemRef = doc(db, 'restaurants', restaurantId, 'menu', itemId);
        const menuItemDoc = await getDoc(menuItemRef);
        
        if (!menuItemDoc.exists()) {
          console.warn('⚠️ Menu item not found:', itemId);
          deductionLog.failures.push({
            itemId,
            reason: 'Menu item not found'
          });
          continue;
        }

        const menuItem = menuItemDoc.data();
        
        // Check if recipe exists
        if (!menuItem.recipe || !menuItem.recipe.ingredients || menuItem.recipe.ingredients.length === 0) {
          console.log('ℹ️ No recipe defined for item:', menuItem.name);
          deductionLog.items.push({
            menuItem: menuItem.name,
            status: 'no_recipe'
          });
          continue;
        }

        // Calculate multiplier based on portion
        let portionMultiplier = 1;
        if (portion === 'Half') {
          portionMultiplier = 0.5;
        }

        // Deduct each ingredient
        for (const ingredient of menuItem.recipe.ingredients) {
          const { inventoryItemId, quantity: recipeQuantity } = ingredient;
          
          // Calculate total quantity to deduct
          const totalDeduction = recipeQuantity * quantity * portionMultiplier;
          
          // Get current inventory item
          const inventoryRef = doc(db, 'restaurants', restaurantId, 'inventory_items', inventoryItemId);
          const inventoryDoc = await getDoc(inventoryRef);
          
          if (!inventoryDoc.exists()) {
            console.warn('⚠️ Inventory item not found:', inventoryItemId);
            deductionLog.failures.push({
              inventoryItemId,
              reason: 'Inventory item not found'
            });
            continue;
          }

          const inventoryItem = inventoryDoc.data();
          const newStock = inventoryItem.currentStock - totalDeduction;
          
          // Update inventory stock
          await updateDoc(inventoryRef, {
            currentStock: Math.max(0, newStock), // Prevent negative stock
            lastDeductionDate: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          });

          // Log successful deduction
          deductionLog.success.push({
            inventoryItem: inventoryItem.name,
            deducted: totalDeduction,
            unit: inventoryItem.unit,
            previousStock: inventoryItem.currentStock,
            newStock: Math.max(0, newStock)
          });

          console.log(`✅ Deducted ${totalDeduction} ${inventoryItem.unit} of ${inventoryItem.name}`);
          
          // Check for low stock alert
          if (newStock <= inventoryItem.minStock) {
            console.warn(`⚠️ LOW STOCK ALERT: ${inventoryItem.name} is at ${newStock} ${inventoryItem.unit}`);
            
            // Create low stock notification
            await this.createLowStockAlert(restaurantId, inventoryItem, newStock);
          }
        }

        deductionLog.items.push({
          menuItem: menuItem.name,
          quantity,
          portion,
          status: 'success'
        });
      }

      // Save deduction log
      await this.saveDeductionLog(restaurantId, orderId, deductionLog);
      
      console.log('✅ Inventory deduction completed:', deductionLog);
      return {
        success: true,
        log: deductionLog
      };
      
    } catch (error) {
      console.error('❌ Error deducting inventory:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Create low stock alert/notification
   */
  async createLowStockAlert(restaurantId, inventoryItem, currentStock) {
    try {
      const alertId = `alert_${Date.now()}_${inventoryItem.id}`;
      const alertRef = doc(db, 'restaurants', restaurantId, 'inventory_alerts', alertId);
      
      await setDoc(alertRef, {
        id: alertId,
        inventoryItemId: inventoryItem.id,
        itemName: inventoryItem.name,
        currentStock,
        minStock: inventoryItem.minStock,
        unit: inventoryItem.unit,
        severity: currentStock === 0 ? 'critical' : 'warning',
        status: 'active',
        createdAt: new Date().toISOString()
      });
      
      console.log('📢 Low stock alert created for:', inventoryItem.name);
    } catch (error) {
      console.error('❌ Error creating low stock alert:', error);
    }
  },

  /**
   * Save deduction log for audit trail
   */
  async saveDeductionLog(restaurantId, orderId, log) {
    try {
      const logRef = doc(db, 'restaurants', restaurantId, 'inventory_logs', orderId);
      await setDoc(logRef, {
        orderId,
        log,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('❌ Error saving deduction log:', error);
    }
  },

  /**
   * Restore inventory when order is cancelled
   */
  async restoreInventoryForCancelledOrder(restaurantId, orderId) {
    try {
      console.log('🔄 Restoring inventory for cancelled order:', orderId);
      
      // Get deduction log
      const logRef = doc(db, 'restaurants', restaurantId, 'inventory_logs', orderId);
      const logDoc = await getDoc(logRef);
      
      if (!logDoc.exists()) {
        console.warn('⚠️ No deduction log found for order:', orderId);
        return { success: false, message: 'No log found' };
      }

      const log = logDoc.data().log;
      
      // Restore each successfully deducted item
      for (const successItem of log.success) {
        // Find inventory item by name (you should store ID in log for better accuracy)
        const inventorySnapshot = await getDocs(
          collection(db, 'restaurants', restaurantId, 'inventory_items')
        );
        
        const inventoryItem = inventorySnapshot.docs.find(
          doc => doc.data().name === successItem.inventoryItem
        );
        
        if (inventoryItem) {
          const inventoryRef = doc(db, 'restaurants', restaurantId, 'inventory_items', inventoryItem.id);
          const currentData = inventoryItem.data();
          
          await updateDoc(inventoryRef, {
            currentStock: currentData.currentStock + successItem.deducted,
            lastRestorationDate: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          });
          
          console.log(`✅ Restored ${successItem.deducted} ${successItem.unit} of ${successItem.inventoryItem}`);
        }
      }
      
      // Mark log as restored
      await updateDoc(logRef, {
        restored: true,
        restoredAt: new Date().toISOString()
      });
      
      return { success: true, message: 'Inventory restored' };
      
    } catch (error) {
      console.error('❌ Error restoring inventory:', error);
      return { success: false, error: error.message };
    }
  }
};

export default InventoryDeductionService;
